// File path: /shell/src/App.tsx
// Shell App - Main Application with Dynamic Module Loading

import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// AWS Configuration
import awsConfig from './aws-config';

// Auth Context Provider (from core)
import { AuthProvider } from '@core/context/AuthContext';

// Subscription Context Provider (from core)
import { SubscriptionProvider } from '@core/context/SubscriptionContext';

// Module Context Provider
import { ModuleProvider } from './context/ModuleContext';

// Shell Components
import ShellLayout from './components/ShellLayout';
import ModuleLoader from './components/ModuleLoader';

// "/" → the user's landing page (Navigate for the roles it serves; D-D)
import RoleBasedRedirect from './routing/RoleBasedRedirect';

// Auth Pages (shell-owned)
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage'));
const NewPasswordRequired = React.lazy(() => import('./pages/auth/NewPasswordRequired'));

// Shared components (from Elevate for backward compatibility)
const NotFoundPage = React.lazy(() => import('@modules/elevate/pages/common/NotFoundPage'));

// Protected Route Component (from Elevate - will move to core eventually)
const ProtectedRoute = React.lazy(() => import('@modules/elevate/components/auth/ProtectedRoute'));

// Initialize Amplify
Amplify.configure(awsConfig);

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <div className="module-loading">
    <div className="module-loading-spinner" />
  </div>
);

// Full page loading for auth pages
const FullPageLoading: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="module-loading-spinner mx-auto mb-4" />
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

const App: React.FC = () => {
  useEffect(() => {
    console.log('LumiLearn Shell App initialized');
  }, []);

  return (
    <AuthProvider>
      <SubscriptionProvider>
        <ModuleProvider>
          <Router>
            <Suspense fallback={<FullPageLoading />}>
              <Routes>
                {/* Auth Routes - not protected */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/new-password-required" element={<NewPasswordRequired />} />

                {/* Protected Routes with Shell Layout */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<ShellLayout />}>
                    {/* Default route - landing page by role and subscription (routing/landingPath.ts) */}
                    <Route path="/" element={<RoleBasedRedirect />} />

                    {/* Legacy URL redirects (for backwards compatibility) */}
                    <Route path="/teacher" element={<Navigate to="/elevate/teacher" replace />} />
                    <Route path="/coach" element={<Navigate to="/elevate/coach" replace />} />
                    <Route path="/schooladmin" element={<Navigate to="/elevate/schooladmin" replace />} />
                    <Route path="/principal/*" element={<Navigate to="/elevate/principal" replace />} />
                    <Route path="/districtadmin" element={<Navigate to="/elevate/districtadmin" replace />} />
                    <Route path="/district/*" element={<Navigate to="/elevate/district" replace />} />
                    <Route path="/admin/*" element={<Navigate to="/elevate/admin" replace />} />
                    <Route path="/programadmin" element={<Navigate to="/elevate/programadmin" replace />} />
                    <Route path="/stateadmin" element={<Navigate to="/elevate/stateadmin" replace />} />
                    <Route path="/superadmin" element={<Navigate to="/elevate/superadmin" replace />} />
                    <Route path="/profile" element={<Navigate to="/elevate/profile" replace />} />
                    <Route path="/feedback" element={<Navigate to="/elevate/feedback" replace />} />
                    <Route path="/resources" element={<Navigate to="/elevate/resources" replace />} />
                    <Route path="/observations/*" element={<Navigate to="/elevate/observations" replace />} />
                    <Route path="/assessments/*" element={<Navigate to="/elevate/assessments/analytics" replace />} />

                    {/* Module Routes - dynamic loading */}
                    <Route path="/:moduleId/*" element={
                      <Suspense fallback={<LoadingFallback />}>
                        <ModuleLoader />
                      </Suspense>
                    } />
                  </Route>
                </Route>

                {/* 404 Not Found */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </Router>
          <ToastContainer position="top-right" autoClose={5000} />
        </ModuleProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
};

export default App;
